import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NbFormFieldModule, NbIconModule, NbInputModule } from '@nebular/theme';
import { NgxMaskDirective } from 'ngx-mask';

/**
 * Campo de data que aceita **digitação** e oferece o calendário.
 *
 * O `input type="date"` sozinho é ruim para data de nascimento no celular: o calendário abre no
 * mês corrente e chegar a 1974 custa dezenas de toques. Aqui o principal é o texto com máscara
 * `dd/mm/aaaa` e teclado numérico (`inputmode`), que resolve data antiga em oito toques; o
 * calendário continua a um toque de distância, no botão ao lado, usando o seletor nativo do
 * aparelho.
 *
 * O valor que entra e sai é sempre ISO (`yyyy-MM-dd`), que é o que a API fala. Data incompleta
 * ou impossível (31/02) emite string vazia: campo de data não inventa valor.
 */
@Component({
  selector: 'app-campo-data',
  standalone: true,
  imports: [CommonModule, FormsModule, NbFormFieldModule, NbIconModule, NbInputModule, NgxMaskDirective],
  templateUrl: './campo-data.component.html',
  styleUrl: './campo-data.component.scss',
})
export class CampoDataComponent {
  @Input() id = 'campo-data';
  @Input() rotulo = '';
  @Input() placeholder = 'dd/mm/aaaa';
  @Input() desabilitado = false;
  /** Preenchido por consulta (CPF, por exemplo): o valor é mostrado, mas não se digita por cima. */
  @Input() somenteLeitura = false;
  @Input() ajuda = '';

  /** Data em ISO (`yyyy-MM-dd`). */
  @Input()
  set valor(iso: string | null | undefined) {
    const novo = iso ?? '';
    if (novo === this.isoAtual) return;
    this.isoAtual = novo;
    this.texto = this.paraBr(novo);
  }

  @Output() valorChange = new EventEmitter<string>();

  @ViewChild('nativo') nativo?: ElementRef<HTMLInputElement>;

  /** O que aparece no campo, em `dd/mm/aaaa`. */
  texto = '';
  private isoAtual = '';

  /** O que o seletor nativo mostra quando abre: a data já escolhida, se houver. */
  get valorIso(): string {
    return this.isoAtual;
  }

  aoDigitar(valor: string): void {
    const digitos = (valor ?? '').replace(/\D/g, '');
    const iso = digitos.length === 8 ? this.paraIso(digitos) : '';

    if (iso === this.isoAtual) return;
    this.isoAtual = iso;
    this.valorChange.emit(iso);
  }

  /** Escolha pelo calendário nativo: chega em ISO e volta para o texto em pt-BR. */
  aoEscolherNoCalendario(iso: string): void {
    if (!iso || iso === this.isoAtual) return;
    this.isoAtual = iso;
    this.texto = this.paraBr(iso);
    this.valorChange.emit(iso);
  }

  /**
   * `showPicker()` abre o calendário sem depender de o campo nativo estar visível; onde ele não
   * existe, o clique no próprio campo faz o papel.
   */
  abrirCalendario(): void {
    if (this.desabilitado || this.somenteLeitura) return;
    const el = this.nativo?.nativeElement;
    if (!el) return;
    if (typeof el.showPicker === 'function') {
      el.showPicker();
      return;
    }
    el.click();
  }

  private paraBr(iso: string): string {
    const partes = (iso ?? '').split('-');
    return partes.length === 3 ? `${partes[2]}/${partes[1]}/${partes[0]}` : '';
  }

  /** `ddMMyyyy` → `yyyy-MM-dd`, recusando o que não é data de calendário (31/02, mês 13). */
  private paraIso(digitos: string): string {
    const dia = Number(digitos.slice(0, 2));
    const mes = Number(digitos.slice(2, 4));
    const ano = Number(digitos.slice(4));

    if (!dia || !mes || mes > 12 || ano < 1900) return '';

    const data = new Date(ano, mes - 1, dia);
    const real =
      data.getFullYear() === ano && data.getMonth() === mes - 1 && data.getDate() === dia;

    return real ? `${String(ano).padStart(4, '0')}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}` : '';
  }
}
